import { Globe, Clock, Settings } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useTimezone } from '@/hooks/useTimezone'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useNavigate } from 'react-router-dom'
import TimezoneIndicator from './TimezoneIndicator'

interface TimezoneContextProps {
  /** Where the context indicator should be placed */
  placement?: 'header' | 'footer' | 'sidebar' | 'inline'
  /** Whether to show a link to timezone settings */
  showSettingsLink?: boolean
  /** Whether to show current time in timezone */
  showCurrentTime?: boolean
  /** Custom className */
  className?: string
  /** Whether to be compact (smaller text, minimal info) */
  compact?: boolean
  /** Custom message prefix */
  message?: string
  /** Whether to show as a dismissible banner */
  isDismissible?: boolean
  /** Callback when dismissed */
  onDismiss?: () => void
}

export function TimezoneContext({
  placement = 'inline',
  showSettingsLink = true,
  showCurrentTime = false,
  className,
  compact = false,
  message = 'All times are displayed in',
  isDismissible = false,
  onDismiss
}: TimezoneContextProps) {
  const navigate = useNavigate()
  const { 
    getDisplayName,
    getAbbreviation,
    getCurrentTime,
    formatTime,
    isLoading 
  } = useTimezone()
  

  if (isLoading) {
    return (
      <div className={cn('text-muted-foreground animate-pulse', getPlacementStyles())}>
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4" />
          <span className="text-sm">Loading timezone...</span>
        </div>
      </div>
    )
  }

  const displayName = getDisplayName()
  const abbreviation = getAbbreviation()
  const currentTime = showCurrentTime ? getCurrentTime() : null

  function getPlacementStyles() {
    const baseStyles = 'flex items-center justify-between gap-4'
    
    switch (placement) {
      case 'header':
        return cn(
          baseStyles,
          'px-4 py-2 bg-muted/30 border-b text-sm',
          className
        )
      case 'footer':
        return cn(
          baseStyles,
          'px-4 py-3 bg-muted/50 border-t text-xs text-muted-foreground',
          className
        )
      case 'sidebar':
        return cn(
          'p-3 border rounded-lg bg-card text-xs',
          className
        )
      case 'inline':
      default:
        return cn(
          'flex items-center gap-2 text-sm text-muted-foreground',
          className
        )
    }
  }

  const handleSettingsClick = () => {
    navigate('/profile')
  }

  // Compact version for space-constrained areas
  if (compact) {
    return (
      <div className={getPlacementStyles()}>
        <div className="flex items-center gap-2">
          <TimezoneIndicator 
            variant="text" 
            size="sm" 
            showTooltip={true}
            className="text-xs"
          />
        </div>
        {showSettingsLink && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSettingsClick}
            className="h-6 px-2 text-xs"
          >
            <Settings className="h-3 w-3" />
          </Button>
        )}
      </div>
    )
  }

  // Full version with more context
  if (!compact && placement === 'header') {
    return (
      <div
        className={cn(
          'flex flex-col items-center gap-3 px-4 py-3 bg-muted/30 border-b text-sm text-center',
          className
        )}
      >
        <div className="flex flex-wrap items-center justify-center gap-3 text-sm font-medium text-foreground">
          <Badge variant="outline" className="text-xs px-3 py-1">
            Times in {abbreviation}
          </Badge>
          {showCurrentTime && currentTime && (
            <span className="flex items-center gap-2 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" />
              <span>Current time: {formatTime(currentTime)}</span>
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2 text-xs text-muted-foreground">
          {showSettingsLink && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSettingsClick}
              className="text-xs px-3 h-7"
            >
              <Settings className="h-3 w-3 mr-1" />
              Change Timezone
            </Button>
          )}
          {isDismissible && onDismiss && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onDismiss}
              className="text-xs px-3 h-7"
            >
              ×
            </Button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={getPlacementStyles()}>
      <div className="flex items-center gap-2">
        <Globe className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm">
          {message} <span className="font-medium">{displayName}</span>
        </span>
        <Badge variant="outline" className="text-xs">
          {abbreviation}
        </Badge>
      </div>
      
      <div className="flex items-center gap-2">
        {showCurrentTime && currentTime && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground border-l pl-3">
            <Clock className="h-3 w-3" />
            <span>
              Current time: {formatTime(currentTime)}
            </span>
          </div>
        )}

        {showSettingsLink && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSettingsClick}
            className="text-xs"
          >
            <Settings className="h-3 w-3 mr-1" />
            Change Timezone
          </Button>
        )}
        
        {isDismissible && onDismiss && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onDismiss}
            className="text-xs"
          >
            ×
          </Button>
        )}
      </div>
    </div>
  )
}

export default TimezoneContext